'use client';

import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { CheckCircle2, Network, Layers, TrendingUp, Sparkles } from 'lucide-react';
import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { cn } from '@/lib/utils';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import Link from 'next/link';

const MLM_PLANS_ICONS = [
    { icon: Network },
    { icon: Layers },
    { icon: TrendingUp },
    { icon: Sparkles },
];

interface Plan {
    name: string;
    slug: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

type PlanRecord = { name: string; slug: string };

export function SmartContractMLM({ initialPlans }: { initialPlans?: PlanRecord[] } = {}) {
    const locale = useLocale();
    const t = useTranslations('homeSmartContract');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });
    const [mlmPlans, setMlmPlans] = React.useState<Plan[]>([
        { name: t('mlmPlans.binary'), icon: MLM_PLANS_ICONS[0].icon, slug: 'binary-mlm-software' },
        { name: t('mlmPlans.matrix'), icon: MLM_PLANS_ICONS[1].icon, slug: 'matrix-plan-mlm-software' },
        { name: t('mlmPlans.unilevel'), icon: MLM_PLANS_ICONS[2].icon, slug: 'unilevel-plan-mlm-software' },
        { name: t('mlmPlans.generation'), icon: MLM_PLANS_ICONS[3].icon, slug: 'generation-plan-mlm-software' },
    ]);

    const features = [
        t('features.feature1'),
        t('features.feature2'),
        t('features.feature3'),
        t('features.feature4'),
        t('features.feature5'),
    ];

    const buildPlanMap = React.useCallback((plans: { name: string; slug: string }[]) => {
        const planMap: Record<string, string> = {};
        plans.forEach((plan) => {
            const nameLower = plan.name.toLowerCase();
            const slugLower = plan.slug.toLowerCase();
            if ((nameLower.includes('binary') || nameLower.includes('binar')) || (slugLower.includes('binary') || slugLower.includes('binar'))) {
                if (!planMap['binary']) planMap['binary'] = plan.slug;
            } else if ((nameLower.includes('matrix') || nameLower.includes('matriz')) || (slugLower.includes('matrix') || slugLower.includes('matriz'))) {
                if (!planMap['matrix']) planMap['matrix'] = plan.slug;
            } else if ((nameLower.includes('unilevel') || nameLower.includes('uninivel') || nameLower.includes('uni-level')) || (slugLower.includes('unilevel') || slugLower.includes('uninivel') || slugLower.includes('uni-level'))) {
                if (!planMap['unilevel']) planMap['unilevel'] = plan.slug;
            } else if ((nameLower.includes('generation') || nameLower.includes('generacion') || nameLower.includes('generazione')) || (slugLower.includes('generation') || slugLower.includes('generacion') || slugLower.includes('generazione'))) {
                if (!planMap['generation']) planMap['generation'] = plan.slug;
            }
        });
        return planMap;
    }, []);

    // SSG: use initialPlans when provided; otherwise fetch for slug mapping
    React.useEffect(() => {
        if (initialPlans?.length) {
            const planMap = buildPlanMap(initialPlans);
            setMlmPlans([
                { name: t('mlmPlans.binary'), icon: MLM_PLANS_ICONS[0].icon, slug: planMap['binary'] || 'binary-mlm-software' },
                { name: t('mlmPlans.matrix'), icon: MLM_PLANS_ICONS[1].icon, slug: planMap['matrix'] || 'matrix-plan-mlm-software' },
                { name: t('mlmPlans.unilevel'), icon: MLM_PLANS_ICONS[2].icon, slug: planMap['unilevel'] || 'unilevel-plan-mlm-software' },
                { name: t('mlmPlans.generation'), icon: MLM_PLANS_ICONS[3].icon, slug: planMap['generation'] || 'generation-plan-mlm-software' },
            ]);
            return;
        }
        const fetchPlans = async () => {
            try {
                const res = await fetch(`/api/mlm-plans?locale=${locale}`, { cache: 'no-store' });
                if (res.ok) {
                    const plans = await res.json();
                    const planMap = buildPlanMap(plans);
                    setMlmPlans([
                        { name: t('mlmPlans.binary'), icon: MLM_PLANS_ICONS[0].icon, slug: planMap['binary'] || 'binary-mlm-software' },
                        { name: t('mlmPlans.matrix'), icon: MLM_PLANS_ICONS[1].icon, slug: planMap['matrix'] || 'matrix-plan-mlm-software' },
                        { name: t('mlmPlans.unilevel'), icon: MLM_PLANS_ICONS[2].icon, slug: planMap['unilevel'] || 'unilevel-plan-mlm-software' },
                        { name: t('mlmPlans.generation'), icon: MLM_PLANS_ICONS[3].icon, slug: planMap['generation'] || 'generation-plan-mlm-software' },
                    ]);
                }
            } catch (error) {
                console.error('Failed to fetch plans for smart contract section:', error);
            }
        };
        fetchPlans();
    }, [locale, t, initialPlans?.length, buildPlanMap]);

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative bg-white dark:bg-black"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="relative z-10">
                <div className="grid lg:grid-cols-[45%_55%]  items-center">
                    {/* Left Section - Graphical Interface */}
                    <SmartContractVisual
                        isVisible={sectionRef.isVisible}
                        mlmPlans={mlmPlans}
                        locale={locale}
                        content={{
                            auditTitle: t('auditSummary.title'),
                            auditDescription: t('auditSummary.description'),
                            auditButton: t('auditSummary.buttonText'),
                            structure: {
                                root: t('mlmStructure.root'),
                                rootLabel: t('mlmStructure.rootLabel'),
                                pay: t('mlmStructure.pay'),
                                payLabel: t('mlmStructure.payLabel'),
                                kyc: t('mlmStructure.kyc'),
                                kycLabel: t('mlmStructure.kycLabel'),
                            },
                        }}
                    />

                    {/* Right Section - Content */}
                    <div
                        className={cn(
                            'transition-all duration-700 lg:pl-[60px]',
                            sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                        )}
                        style={{ transitionDelay: '200ms' }}
                    >
                        <SectionHeading
                            pill={t('pillLabel')}
                            title={t('title')}
                            subtitle={t('subtitle')}
                            isVisible={sectionRef.isVisible}
                            align="left"
                            maxWidthClass="max-w-3xl"
                        />

                        {/* Features List */}
                        <ul className="space-y-4 mb-8">
                            {features.map((feature, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                                    <Typography
                                        variant="p"
                                        className="text-gray-700 dark:text-gray-300 leading-relaxed"
                                    >
                                        {feature}
                                    </Typography>
                                </li>
                            ))}
                        </ul>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 items-center md:items-start">
                            <Button
                                href={getLocalizedPagePath('free-mlm-software-demo', locale)}
                                variant="outline"
                                size="md"
                                className="w-auto justify-center"
                            >
                                {t('demoButton')}
                            </Button>
                            <Button
                                href={getLocalizedPagePath('pricing', locale)}
                                variant="primary"
                                size="md"
                                className="w-auto justify-center"
                            >
                                {t('pricingButton')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

type VisualContent = {
    auditTitle: string;
    auditDescription: string;
    auditButton: string;
    structure: {
        root: string;
        rootLabel: string;
        pay: string;
        payLabel: string;
        kyc: string;
        kycLabel: string;
    };
};

function SmartContractVisual({
    isVisible,
    mlmPlans,
    locale,
    content,
}: {
    isVisible: boolean;
    mlmPlans: { name: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; slug: string }[];
    locale: string;
    content: VisualContent;
}) {
    return (
        <div
            className={cn(
                'relative transition-all duration-700',
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            )}
        >
            {/* Background Window */}
            <div className="relative bg-gray-200 dark:bg-gray-800 rounded-xl p-8 shadow-2xl min-h-[400px]">
                {/* Popup Card */}
                <div className="relative bg-white dark:bg-gray-900 rounded-lg p-6 shadow-xl border border-gray-200 dark:border-gray-700 -ml-4 -mt-4 z-10">
                    {/* Dashed connector line */}
                    <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 border-t-2 border-dashed border-gray-300 dark:border-gray-600"></div>

                    <Typography variant="p" className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                        {content.auditTitle}
                    </Typography>
                    <Typography variant="p" className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {content.auditDescription}
                    </Typography>

                    {/* Avatars */}
                    <div className="flex gap-2 mb-4">
                        {[
                            { bg: 'bg-blue-500', initial: 'SC' },
                            { bg: 'bg-emerald-500', initial: 'TX' },
                            { bg: 'bg-indigo-500', initial: 'KYC' },
                            { bg: 'bg-amber-500', initial: 'AML' },
                        ].map((avatar, i) => (
                            <div
                                key={i}
                                className={cn(
                                    'w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-semibold',
                                    avatar.bg
                                )}
                            >
                                {avatar.initial}
                            </div>
                        ))}
                    </div>

                    {/* Audit Text */}
                    <div className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg font-medium text-center">
                        {content.auditButton}
                    </div>
                </div>

                {/* MLM Structure Visualization - Background */}
                <div className="mt-8 relative">
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700 opacity-80">
                        {/* Simple MLM tree representation */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-xs">
                                    {content.structure.root}
                                </div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">{content.structure.rootLabel}</span>
                            </div>
                            <div className="flex gap-8">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-semibold text-xs">
                                        {content.structure.pay}
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">{content.structure.payLabel}</span>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-xs">
                                        {content.structure.kyc}
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">{content.structure.kycLabel}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MLM Plans Grid - Background Card */}
            <div className="absolute -right-4 -bottom-4 bg-primary-600 dark:bg-primary-700 rounded-xl p-4 shadow-2xl w-44 z-20">
                <div className="grid grid-cols-2 gap-2">
                    {mlmPlans.map((plan) => {
                        const Icon = plan.icon;
                        // Use plan slug directly from API - plans are handled by catch-all route [slug]
                        const planHref = getLocalizedPath(plan.slug, locale);
                        return (
                            <Link
                                key={plan.name}
                                href={planHref}
                                className="bg-white/10 hover:bg-white/20 rounded-lg p-2.5 text-center transition-colors cursor-pointer"
                            >
                                <Icon className="h-4 w-4 text-white mx-auto mb-1" />
                                <span className="text-[11px] leading-tight text-white font-medium block text-center whitespace-normal break-words">
                                    {plan.name}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

